import React, { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import idConfig from 'utils/ecommerce/services/id.config';
import CustomRadio from 'components/CustomRadio/CustomRadio';
import CustomLink from 'components/CustomLink/CustomLink';
import AlternativeSelect from 'components/AlternativeSelect/AlternativeSelect';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { finalize } from 'rxjs/operators';
import { translate } from 'components/Translation';
import styles from 'assets/jss/material-dashboard-pro-react/views/services/addLineDialogStyle';
import { makeStyles } from '@material-ui/core';
import { serviceType } from 'views/AddNewServices/AddSingleService/constants';
import { BodyM, BodyS, DescriptionM } from 'components/Typography/Typography';
import classNames from 'classnames';
import VatClause from 'components/VatClause/VatClause';
import { ProductsService } from 'services/products/products';
import { SingleCustomerProduct } from 'utils/api/models/products/singleCustomerProduct';
import { NumbersService } from 'services/numbers/numbers';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import apiConfig from 'utils/api/config';
import { BasicPlan, ProductsDetailsResponse } from 'utils/api/models/postpaidLines/postpaidLines';
import { MobileInternetServices } from 'utils/api/models/products/mobileInternetServices';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IServiceTabProps {
	selectedOrderItems: types.newServices.IOrderItem[];
	setSelectedOrderItems: (e: any) => void;
	showError: boolean;
	baseNumber: types.newServices.IBaseNumber | null;
	availableServices: number[];
	namaBundleCommitmentId: string;
	tariffPlanId: string;
	productsDetails: ProductsDetailsResponse | null;
	selectedTariff: BasicPlan | null;
}
const ServicesTab = ({
	selectedOrderItems,
	setSelectedOrderItems,
	showError,
	baseNumber,
	availableServices,
	namaBundleCommitmentId,
	tariffPlanId,
	productsDetails,
	selectedTariff,
}: IServiceTabProps) => {
	const classes = useStyles();
	const [mobileInternetServices, setMobileInternetServices] = useState<MobileInternetServices[]>([]);
	const [customerProducts, setCustomerProducts] = useState<SingleCustomerProduct[]>([]);
	const [loading, setLoading] = useState({
		packages: false,
		commitments: false,
		tariffPlans: false,
		customerProducts: false,
	});
	const [commitmentsWithBuiltInInternationalAndRoaming, setCommitmentsWithBuiltInInternationalAndRoaming] = useState<
		string[]
	>([]);
	const [tariffPlansWithBuiltInInternationalAndRoaming, setTariffPlansWithBuiltInInternationalAndRoaming] = useState<
		string[]
	>([]);
	const [mobileInternetPlanPickerVisible, setMobileInternetPlanPickerVisible] = useState(
		availableServices.indexOf(serviceType.MOBILE_INTERNET_PLANS) >= 0
	);
	const [isRoamingInSelectedTariff, setIsRoamingInSelectedTariff] = useState<boolean>(false);
	const [showNoServiceMessage, setNoServiceMessage] = useState<boolean>(false);

	const gsmServicesVisible = availableServices.indexOf(serviceType.GSM) >= 0;
	const cpnVisible = availableServices.indexOf(serviceType.CPN) >= 0;
	const optionalVisible = gsmServicesVisible || cpnVisible;
	const isMounted = useRef(true);

	const place = 'OM';

	const gsmServices = [
		{
			name: translate('none'),
			id: 'GSM_NONE',
			orderItems: [],
		},
		{
			name: translate('number.services.international.roaming'),
			id: 'ROAMING_INTERNATIONAL',
			orderItems: [idConfig.ROAMING, idConfig.INTERNATIONAL].map((service) => ({
				...service,
				type: serviceType.GSM,
			})),
		},
		{
			name: translate('number.services.international'),
			id: 'INTERNATIONAL',
			orderItems: [idConfig.INTERNATIONAL].map((service) => ({
				...service,
				type: serviceType.GSM,
			})),
		},
	];

	const isServiceChecked = (service: any, type: string | number) => {
		const _selectedOrderItems = selectedOrderItems.filter((item) => item.type === type);
		return _.xorBy(_selectedOrderItems, service.orderItems, 'legacyProductId').length <= 0;
	};

	const checkService = (service: any, type: string | number) => {
		const _selectedOrderItems = selectedOrderItems.filter((item) => item.type !== type);
		if (type === serviceType.MOBILE_INTERNET_PLANS && service) {
			_selectedOrderItems.push(service);
		} else if (type === serviceType.GSM) {
			_selectedOrderItems.push(...service.orderItems);
		}
		setSelectedOrderItems(_selectedOrderItems);
	};

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (baseNumber) {
			setLoading((loading) => ({ ...loading, customerProducts: true }));
			ProductsService.getCustomerProducts({
				phoneNumber: baseNumber?.accountNum,
				accountType: baseNumber?.accountType,
				billingType: baseNumber?.billingType,
			}).subscribe(
				({ products }) => {
					setCustomerProducts(products);
					setLoading((loading) => ({ ...loading, customerProducts: false }));
				},
				() => {
					setLoading((loading) => ({ ...loading, customerProducts: false }));
				}
			);
		}
	}, [baseNumber]);

	useEffect(() => {
		setLoading((loading) => ({ ...loading, packages: true }));
		if (productsDetails && tariffPlanId) {
			const packages = productsDetails.commercialProducts[
				productsDetails.commercialProducts.findIndex((pd) => pd.productGroup === 'MBB_PACKAGE_CORP')
			]?.products
				.filter((product) => product.requiredTariffPlans.includes(tariffPlanId))
				.map((singlePackage) => ({
					name: singlePackage.productName,
					legacyProductId: singlePackage.productId,
					place: place,
					id: singlePackage.productId,
					type: serviceType.MOBILE_INTERNET_PLANS,
				}));
			if (packages?.length) {
				setMobileInternetServices(packages);
			} else {
				setMobileInternetPlanPickerVisible(false);
				setNoServiceMessage(true);
			}
		}

		setLoading((loading) => ({ ...loading, packages: false }));
	}, [customerProducts, productsDetails, tariffPlanId]);

	useEffect(() => {
		setLoading((loading) => ({ ...loading, commitments: true }));
		NumbersService.getCommitmentsWithBuiltInInternationalAndRoaming()
			.pipe(finalize(() => isMounted.current && setLoading((loading) => ({ ...loading, commitments: false }))))
			.subscribe(({ response }) => setCommitmentsWithBuiltInInternationalAndRoaming(response.ids || []));
	}, []);

	useEffect(() => {
		setLoading((loading) => ({ ...loading, tariffPlans: true }));
		NumbersService.getTariffPlansWithBuiltInInternationalAndRoaming()
			.pipe(finalize(() => isMounted.current && setLoading((loading) => ({ ...loading, tariffPlans: false }))))
			.subscribe(({ response }) => setTariffPlansWithBuiltInInternationalAndRoaming(response.ids || []));
	}, []);

	useEffect(() => {
		if (selectedTariff && selectedTariff.roaming) {
			setIsRoamingInSelectedTariff(true);
		}
	}, [selectedTariff]);

	const isCommitmentWithBuildInInternationalAndRoaming = useMemo(() => {
		return commitmentsWithBuiltInInternationalAndRoaming.indexOf(namaBundleCommitmentId) >= 0;
	}, [commitmentsWithBuiltInInternationalAndRoaming, namaBundleCommitmentId]);

	const isTariffPlanWithBuildInInternationalAndRoaming = useMemo(() => {
		return tariffPlansWithBuiltInInternationalAndRoaming.indexOf(tariffPlanId) >= 0;
	}, [tariffPlansWithBuiltInInternationalAndRoaming, tariffPlanId]);

	const selectedMobileInternetPlan = useMemo(() => {
		return selectedOrderItems.find((item) => item.type === serviceType.MOBILE_INTERNET_PLANS) || null;
	}, [selectedOrderItems]);

	const mobileInternetPlanError =
		!selectedMobileInternetPlan && showError ? translate('add.new.line.internet.plan.not.selected') : null;

	return (
		<div className={classNames(classes.servicesTab, classes.smWidth)}>
			<ConditionalRender
				show={Object.values(loading).some(Boolean)}
				onTrue={() => <CustomCircularProgress text={''} loading transparent />}
				onFalse={() => (
					<ConditionalRender
						show={showNoServiceMessage}
						onFalse={() => (
							<>
								<ConditionalRender show={mobileInternetPlanPickerVisible}>
									<div className={classes.mobileInternetPlans}>
										<BodyM color="primary-light">
											{translate('number.services.mobile.internet.plans')}
										</BodyM>
										<AlternativeSelect
											label={translate('number.services.mobile.internet.plan')}
											id="mobile-internet-plan-select"
											value={
												<div className={classes.selectedItem}>
													<BodyS color="primary-light">
														{selectedMobileInternetPlan?.name || translate('none')}
													</BodyS>
												</div>
											}
											onClick={(service) =>
												checkService(service, serviceType.MOBILE_INTERNET_PLANS)
											}
											items={mobileInternetServices}
											selectedItems={selectedOrderItems
												.filter((item) => item.type === serviceType.MOBILE_INTERNET_PLANS)
												.map((item) => item.legacyProductId)}
											clear={() => checkService(null, serviceType.MOBILE_INTERNET_PLANS)}
											error={mobileInternetPlanError as string}
										/>
										<BodyS color="disabled-light">
											{translate('number.services.mobile.internet.terms')}
										</BodyS>
										<CustomLink
											to={apiConfig.links.mobileInternet}
											color="brand"
											type="bodyS"
											target="_blank"
										>
											{translate('number.services.terms.conditions')}
										</CustomLink>
									</div>
								</ConditionalRender>
								<ConditionalRender show={optionalVisible && !isRoamingInSelectedTariff}>
									<BodyM color="primary-light">
										{translate('add.new.line.choose.extra.services')}
									</BodyM>
								</ConditionalRender>
								<ConditionalRender
									show={
										!isCommitmentWithBuildInInternationalAndRoaming &&
										!isTariffPlanWithBuildInInternationalAndRoaming &&
										!isRoamingInSelectedTariff
									}
									onTrue={() => (
										<ConditionalRender show={gsmServicesVisible}>
											<div className={classes.gsmServices}>
												<BodyM color="secondary-light">
													{translate('number.services.mobile.gsm.services')}
												</BodyM>
												<div>
													{gsmServices.map((service) => (
														<div key={service.id}>
															<CustomRadio
																checked={isServiceChecked(service, serviceType.GSM)}
																label={service.name}
																onChange={() => checkService(service, serviceType.GSM)}
															/>
														</div>
													))}
												</div>
											</div>
										</ConditionalRender>
									)}
								/>
								<ConditionalRender
									show={
										isCommitmentWithBuildInInternationalAndRoaming ||
										isTariffPlanWithBuildInInternationalAndRoaming
									}
									onTrue={() => (
										<div className={classes.builtInInternationalRoaming}>
											{translate(
												'number.services.mobile.tariff.plan.with.built.in.international.roaming'
											)}
										</div>
									)}
								/>
								<VatClause />
							</>
						)}
						onTrue={() => (
							<DescriptionM customClassName={classes.NoServices}>
								{translate('number.services.unavailable')}
							</DescriptionM>
						)}
					></ConditionalRender>
				)}
			/>
		</div>
	);
};

export default ServicesTab;
